// Installation-specific overrides and special cases

import { STRIPE_SUBSCRIPTION_STATUS } from '@/types/subscription';

/**
 * Personio-Internal installation ID
 * Special case: PR description generation should always be disabled
 */
export const PERSONIO_INTERNAL_INSTALLATION_ID = 85287978;

/**
 * Wayflyer installation ID.
 * Temporary special case: allow the pro private wiki cap.
 */
export const WAYFLYER_INSTALLATION_ID = 111148054;

/**
 * Coalition linked GitHub Enterprise installations.
 * Temporary special case: keep Stripe-backed AI review access alive while
 * accounting finishes payment processing.
 */
export const COALITION_BINARYEDGE_INSTALLATION_ID = 125570596;
export const COALITION_SRE_INSTALLATION_ID = 137479782;
export const COALITION_CRUCIBLE_RISK_INSTALLATION_ID = 125869472;

/**
 * Parloa linked GitHub Enterprise installations.
 * Temporary special case: keep Stripe-backed AI review access alive while
 * accounting finishes payment processing.
 */
export const PARLOA_INSTALLATION_ID = 121365808;
export const PARLOA_SANDBOX_INSTALLATION_ID = 146258334;

/**
 * Life in Flow linked installations.
 * Temporary special case: keep Stripe-backed AI review access alive while
 * Stripe retries the failed subscription payment.
 */
export const LIFE_IN_FLOW_INSTALLATION_ID = 105468409;
export const LIFE_IN_FLOW_BUSINESS_INSTALLATION_ID = 111002742;

/**
 * Omnea Digital installation.
 * Temporary special case: keep Stripe-backed AI review access alive while
 * Stripe retries the failed subscription payment.
 */
export const OMNEA_DIGITAL_INSTALLATION_ID = 133717594;

/** Daytona linked installations sharing the same Stripe subscription. */
export const DAYTONA_INSTALLATION_ID = 142645874;
export const DAYTONAIO_INSTALLATION_ID = 116769542;

/**
 * ErbBank installation ID.
 * Temporary special case: allow a larger private wiki trial while the team evaluates Cubic.
 */
export const ERBBANK_INSTALLATION_ID = 126791348;

/**
 * Satispay Tech installation ID.
 * Temporary special case: allow a larger private wiki trial while the team evaluates Cubic.
 */
export const SATISPAY_TECH_INSTALLATION_ID = 149854013;

/**
 * jollypoints installation ID.
 * Temporary special case: allow one extra private wiki while the team evaluates Cubic.
 */
export const JOLLYPOINTS_INSTALLATION_ID = 164123470;

/**
 * WeTravel installation ID.
 * Temporary special case: keep paid-trial reviews on premium model treatment
 * for 30 days after upgrade while the team evaluates Cubic.
 */
export const WETRAVEL_INSTALLATION_ID = 130601145;

/**
 * Helloprint installation ID.
 * Paid for 3 additional codebase scan repository slots.
 */
export const HELLOPRINT_INSTALLATION_ID = 121885117;

/**
 * Unstructured installation ID.
 * Enterprise agreement includes 5 codebase scan repository slots.
 */
export const UNSTRUCTURED_INSTALLATION_ID = 130051100;

/**
 * Rad AI Systems installation ID.
 * Team plan add-on includes 6 codebase scan repository slots.
 */
export const RAD_AI_SYSTEMS_INSTALLATION_ID = 136141128;

/**
 * Installations where GitHub outside collaborators should not be able to access
 * the Cubic portal/UI, even when they have repo access in GitHub.
 */
export const OUTSIDE_COLLABORATOR_PORTAL_BLOCK_INSTALLATION_IDS: ReadonlySet<number> = new Set([
  UNSTRUCTURED_INSTALLATION_ID,
]);

export function shouldBlockOutsideCollaboratorPortalAccess(
  installationId: number | null | undefined,
): boolean {
  return (
    installationId !== null &&
    installationId !== undefined &&
    OUTSIDE_COLLABORATOR_PORTAL_BLOCK_INSTALLATION_IDS.has(installationId)
  );
}

/**
 * defi-wonderland installation ID.
 * High-priority customer with explicit AI review quality overrides.
 */
export const DEFI_WONDERLAND_INSTALLATION_ID = 112359231;

/**
 * modelcontextprotocol installation ID.
 * High-priority customer with explicit AI review quality overrides.
 */
export const MODELCONTEXTPROTOCOL_INSTALLATION_ID = 142568605;

/**
 * Installations that should always resolve to the super-premium review route.
 * Today this routes eligible reviews onto the stable ordered-files grouped lane.
 */
const SUPER_PREMIUM_INSTALLATION_OVERRIDES: ReadonlySet<number> = new Set([
  DEFI_WONDERLAND_INSTALLATION_ID,
  MODELCONTEXTPROTOCOL_INSTALLATION_ID,
]);

/**
 * Temporary manual Stripe extension for Personio-Internal.
 * Remove this after the temporary grace window expires.
 */
const PERSONIO_INTERNAL_STRIPE_GRACE_PERIOD_EXPIRES_AT = new Date('2026-08-20T23:59:59.000Z');

/**
 * Check if an installation should have PR description generation disabled
 */
export function shouldDisablePrDescriptionGeneration(installationId: number): boolean {
  return installationId === PERSONIO_INTERNAL_INSTALLATION_ID;
}

export function shouldForceSuperPremiumReviewForInstallation(
  installationId: number | null | undefined,
): boolean {
  return (
    installationId !== null &&
    installationId !== undefined &&
    SUPER_PREMIUM_INSTALLATION_OVERRIDES.has(installationId)
  );
}

/**
 * Temporary manual Stripe extension for Coalition linked orgs.
 * Remove this as soon as payment is resolved.
 */
const COALITION_STRIPE_GRACE_PERIOD_EXPIRES_AT = new Date('2026-09-08T23:59:59.000Z');

/**
 * Temporary manual Stripe extension for Parloa linked orgs.
 * Remove this as soon as payment is resolved.
 */
const PARLOA_STRIPE_GRACE_PERIOD_EXPIRES_AT = new Date('2026-09-22T23:59:59.000Z');

/**
 * Temporary manual Stripe extension for Life in Flow linked orgs.
 * Remove this as soon as payment is resolved.
 */
const LIFE_IN_FLOW_STRIPE_GRACE_PERIOD_EXPIRES_AT = new Date('2026-08-24T23:59:59.000Z');

/**
 * Temporary manual Stripe extension for Omnea Digital.
 * Remove this as soon as payment is resolved.
 */
const OMNEA_DIGITAL_STRIPE_GRACE_PERIOD_EXPIRES_AT = new Date('2026-09-10T23:59:59.000Z');

/** Temporary seven-day Stripe grace for Daytona while payment is resolved. */
const DAYTONA_STRIPE_GRACE_PERIOD_EXPIRES_AT = new Date('2026-09-18T10:42:30.000Z');

type TemporaryStripeGraceOverride = {
  expiresAt: Date;
  allowedStatuses: ReadonlySet<string>;
};

const TEMPORARY_STRIPE_GRACE_ALLOWED_STATUSES: ReadonlySet<string> = new Set([
  STRIPE_SUBSCRIPTION_STATUS.PAUSED,
  STRIPE_SUBSCRIPTION_STATUS.PAST_DUE,
  STRIPE_SUBSCRIPTION_STATUS.UNPAID,
]);

function createTemporaryStripeGraceOverride(expiresAt: Date): TemporaryStripeGraceOverride {
  return {
    expiresAt,
    allowedStatuses: TEMPORARY_STRIPE_GRACE_ALLOWED_STATUSES,
  };
}

const TEMPORARY_STRIPE_GRACE_PERIOD_OVERRIDES = new Map<number, TemporaryStripeGraceOverride>([
  [
    DAYTONA_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(DAYTONA_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    DAYTONAIO_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(DAYTONA_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    PERSONIO_INTERNAL_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(PERSONIO_INTERNAL_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    COALITION_BINARYEDGE_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(COALITION_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    COALITION_SRE_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(COALITION_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    COALITION_CRUCIBLE_RISK_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(COALITION_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    PARLOA_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(PARLOA_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    PARLOA_SANDBOX_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(PARLOA_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    LIFE_IN_FLOW_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(LIFE_IN_FLOW_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    LIFE_IN_FLOW_BUSINESS_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(LIFE_IN_FLOW_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
  [
    OMNEA_DIGITAL_INSTALLATION_ID,
    createTemporaryStripeGraceOverride(OMNEA_DIGITAL_STRIPE_GRACE_PERIOD_EXPIRES_AT),
  ],
]);

export function hasTemporaryStripeGraceAccess(
  installationId: number,
  subscriptionStatus: string,
  now: Date = new Date(),
): boolean {
  const override = TEMPORARY_STRIPE_GRACE_PERIOD_OVERRIDES.get(installationId);

  if (!override) {
    return false;
  }

  if (now > override.expiresAt) {
    return false;
  }

  return override.allowedStatuses.has(subscriptionStatus);
}

/**
 * Installations with non-default codebase scan repository caps.
 */
export const REPO_SCAN_CAP_INSTALLATION_OVERRIDES = new Map<number, number>([
  // Syntage installation: default 3 plus 8 additional repositories.
  [155531010, 11],
  // Resend installation: allow up to 30 repositories to be scanned.
  [81138909, 30],
  // Helloprint installation: default 3 plus 4 additional paid repositories.
  [HELLOPRINT_INSTALLATION_ID, 7],
  // Unstructured installation: enterprise agreement includes 5 repositories.
  [UNSTRUCTURED_INSTALLATION_ID, 5],
  // Rad AI Systems installation: Team plan add-on includes 6 repositories.
  [RAD_AI_SYSTEMS_INSTALLATION_ID, 6],
]);

export function getRepoScanCapOverride(installationId: number): number | null {
  return REPO_SCAN_CAP_INSTALLATION_OVERRIDES.get(installationId) ?? null;
}

/**
 * Installations with non-default private wiki caps.
 */
export const PRIVATE_WIKI_LIMIT_INSTALLATION_OVERRIDES = new Map<number, number>([
  // Wayflyer installation: temporarily allow the pro private wiki cap.
  [WAYFLYER_INSTALLATION_ID, 20],
  // ErbBank installation: allow trialing the main repo after generating a small repo wiki.
  [ERBBANK_INSTALLATION_ID, 3],
  // Satispay Tech installation: allow up to five private wikis during the trial.
  [SATISPAY_TECH_INSTALLATION_ID, 5],
  // jollypoints installation: trial cap of one plus one extra private wiki.
  [JOLLYPOINTS_INSTALLATION_ID, 2],
]);

export function getPrivateWikiLimitOverride(installationId: number): number | null {
  return PRIVATE_WIKI_LIMIT_INSTALLATION_OVERRIDES.get(installationId) ?? null;
}

/**
 * Installations with a non-default paid premium model treatment window.
 */
export const PAID_PREMIUM_TREATMENT_WINDOW_DAYS_INSTALLATION_OVERRIDES = new Map<number, number>([
  // WeTravel paid trial: keep premium model treatment for the first 30 paid days.
  [WETRAVEL_INSTALLATION_ID, 30],
]);

export function getPaidPremiumTreatmentWindowDaysOverride(installationId: number): number | null {
  return PAID_PREMIUM_TREATMENT_WINDOW_DAYS_INSTALLATION_OVERRIDES.get(installationId) ?? null;
}
